import React, { useEffect, useState } from "react";
import {
  Steps,
  Input,
  Select,
  Button,
  Upload,
  DatePicker,
  Row,
  Col,
  Divider,
  Switch,
  Checkbox,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "./index.css";
import { useAxiosPrivate } from "hooks/useAxiosPrivate";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useParams } from "react-router-dom";
import TextArea from "antd/es/input/TextArea";
const { Option } = Select;

const OCFMultiStepForm = () => {
  const [current, setCurrent] = useState(0);
  const LAST_STEP = 4;

  const ocfSchema = yup.object({
    applicant: yup.object({
      name: yup.string().trim().required("Customer name is required"),
      careOfName: yup.string().trim().required("Father/Husband is required"),
      gender: yup.string().required("Gender is required"),
      email: yup.string().email("Invalid email").required("Email is required"),
      mobile: yup
        .string()
        .matches(/^[6-9]\d{9}$/, "Enter valid mobile")
        .required("Mobile is required"),
      mobile2: yup
        .string()
        .matches(/^[6-9]\d{9}$/, "Enter valid alternate mobile")
        .nullable(),
      dob: yup.string().required("DOB is required"),
      address: yup.string().required("Address is required"),
      profession: yup.string().nullable(),
      aadhar: yup
        .string()
        .matches(/^\d{12}$/, "Aadhar must be 12 digits")
        .required("Aadhar required"),
      pan: yup
        .string()
        .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN")
        .required("PAN required"),
      aadharFile: yup.mixed().required("Aadhar copy required"),
      panFile: yup.mixed().required("PAN copy required"),
    }),
    coApplicant: yup
      .object({
        name: yup.string().trim(),
        careOfName: yup.string().trim(),
        gender: yup.string(),
        email: yup.string().email("Invalid email").nullable(), // optional
        mobile: yup.string().matches(/^[6-9]\d{9}$/, "Enter valid mobile"),
        mobile2: yup.string().matches(/^[6-9]\d{9}$/, "Enter valid alternate mobile"),
        dob: yup.string(),
        address: yup.string(),
        profession: yup.string().nullable(),
        aadhar: yup.string().matches(/^\d{12}$/, "Aadhar must be 12 digits"),
        pan: yup.string().matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN"),
        aadharFile: yup.mixed(),
        panFile: yup.mixed(),
        profession: yup.string().nullable(),
      })
      .test(
        "coApplicant-required-if-any",
        "All co-applicant fields are required if one is filled",
        function (value) {
          if (!value) return true;

          // remove email & profession (optional fields)
          const requiredKeys = [
            "name",
            "careOfName",
            "gender",
            "mobile",
            "mobile2",
            "dob",
            "address",
            "aadhar",
            "pan",
            "aadharFile",
            "panFile",
          ];

          const hasAnyValue = requiredKeys.some(
            (key) => value[key] !== undefined && value[key] !== null && value[key] !== ""
          );

          // if nothing filled → OK
          if (!hasAnyValue) return true;

          // if some filled → all required
          const missing = requiredKeys.filter(
            (key) => value[key] === undefined || value[key] === null || value[key] === ""
          );

          if (missing.length > 0) {
            return this.createError({
              message: `Fill all co-applicant fields`,
            });
          }

          return true;
        }
      )
      .nullable(),

    /* STEP 3 */
    propertyDetails: yup.string().required("Property is required"),
    paymentPlan: yup.string().required("Payment plan is required"),

    inventoryDetail: yup.object({
      unitNo: yup.string().required("Unit No is required."),
      floorNo: yup.string().required("Floor No is required."),
      towerNo: yup.string().required("Tower No is required."),
    }),

    commission: yup.object({
      value: yup
        .number()
        .typeError("Commission value must be a number")
        .required("Commission value is required")
        .min(0, "Cannot be negative"),
      com_unit: yup.string().required("Commission unit is required"), // %, ₹, per sq ft, etc
      is_discount: yup.boolean().required(),
      discount: yup
        .number()
        .nullable()
        .when("is_discount", {
          is: true,
          then: (s) =>
            s
              .typeError("Discount must be a number")
              .required("Discount is required")
              .min(0, "Cannot be negative"),
          otherwise: (s) => s.nullable().notRequired(),
        }),
      dis_unit: yup
        .string()
        .nullable()
        .when("is_discount", {
          is: true,
          then: (s) => s.required("Discount unit is required"),
          otherwise: (s) => s.notRequired(),
        }),
      dis_approval_file: yup
        .mixed()
        .nullable()
        .when("is_discount", {
          is: true,
          then: (s) => s.required("Discount approval file is required"),
          otherwise: (s) => s.notRequired(),
        }),
      net_income: yup.string().nullable(), // usually calculated field → optional
    }),

    payment: yup
      .array()
      .of(
        yup.object({
          type: yup.string().required("Type is required"),

          amount: yup
            .number()
            .typeError("Amount must be number")
            .required("Amount is required")
            .min(0, "Cannot be negative"),

          receiptNo: yup.string().required("Receipt No is required"),

          confirmation: yup.boolean().required(),

          date: yup.string().required("Date is required"),
        })
      )
      .min(1, "At least one payment required"),

    /* STEP 4 */
    commission: yup
      .number()
      .typeError("Commission must be a number")
      .required("Commission is required"),

    managerApproval: yup.mixed().required("Manager approval file is required"),
  });

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors, dirtyFields },
  } = useForm({
    resolver: current === LAST_STEP ? yupResolver(ocfSchema) : undefined,
    mode: "onSubmit",
    shouldUnregister: false,
  });

  const {
    fields: paymentFields,
    append: addPayment,
    remove: removePayment,
  } = useFieldArray({
    control,
    name: "payment",
  });

  const { id } = useParams();

  const steps = [
    { title: "Customer" },
    { title: "Co-Applicant" },
    { title: "Property Details" },
    { title: "Costing" },
    { title: "Payment" },
  ];

  const stepFieldMap = {
    0: [
      "applicant.name",
      "applicant.careOfName",
      "applicant.gender",
      "applicant.email",
      "applicant.mobile",
      "applicant.mobile2",
      "applicant.dob",
      "applicant.address",
      "applicant.aadharFile",
      "applicant.aadhar",
      "applicant.pan",
      "applicant.panFile",
    ],
    1: [
      "coApplicant.name",
      "coApplicant.gender",
      "coApplicant.mobile",
      "coApplicant.mobile2",
      "coApplicant.email",
      "coApplicant.dob",
      "coApplicant.address",
      "coApplicant.pan",
      "coApplicant.panFile",
      "coApplicant.aadhar",
      "coApplicant.aadharFile",
    ],

    2: ["propertyDetails", "paymentPlan"],
    3: ["managerApproval"],
    4: ["payment"],
  };

  const hasStepError = (stepIndex, errors) => {
    return stepFieldMap[stepIndex].some((field) => errors[field]);
  };
  const stepItems = steps.map((step, index) => {
    let status = "wait";
    if (index < current) status = "finish";
    if (index === current) status = "process";
    if (hasStepError(index, errors)) {
      status = "error"; // 🔥 RED STEP
    }
    return {
      title: step.title,
      status,
    };
  });

  const saveStepDraft = async (stepIndex) => {
    const stepFields = stepFieldMap[stepIndex];
    const values = getValues();
    const changedFields = stepFields.filter((f) => dirtyFields[f]);
    if (changedFields.length === 0) {
      return true;
    }
    const payload = {};
    changedFields.forEach((f) => {
      payload[f] = values[f];
    });

    // try {
    //   await axiosPrivate({
    //     url: "/ocf/saveDraft",
    //     method: "post",
    //     data: payload,
    //   });

    //   // ✅ clear dirty state
    //   reset(values);

    //   return true;
    // } catch (e) {
    //   console.log(e);
    //   return false;
    // }
  };

  const next = async () => {
    const success = await saveStepDraft(current);
    if (!success) return;
    setCurrent((p) => p + 1);
  };

  const prev = () => setCurrent((p) => p - 1);

  const onSubmit = (data) => {
    console.log("✅ FULL FORM VALIDATED DATA", data);
  };


  const Error = ({ name }) => {
    if (!errors[name]) return null;
    return (
      <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: 4 }}>
        {errors[name]?.message}
      </div>
    );
  };

  const axiosPrivate = useAxiosPrivate();
  const [propertyConfig, setPropertyConfig] = useState();
  const getPropertyConfig = () => {
    const option = {
      url: `om/masterConfig/ocf`,
      method: "get",
    };
    axiosPrivate(option)
      .then((response) => {
        setPropertyConfig(response?.data?.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };
  const [ocfDetails, setOcfDetails] = useState({});
  const getOcfDetails = (id) => {
    const option = {
      url: `om/ocfDetails?ocfId=${id}`,
      method: "get",
    };
    axiosPrivate(option)
      .then((response) => {
        setOcfDetails(response?.data?.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    getPropertyConfig();
    getPropertyList();
    getOcfDetails(id);
  }, []);

  const handleFileUpload = () => {
    const formData = new FormData();
    const values = getValues();
    Object.keys(values).forEach((key) => {
      formData.append(key, values[key]);
    });
    axiosPrivate({
      url: "/ocf/submit",
      method: "POST",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data", // optional but OK
      },
    });
  };

  const UploadField = ({ name, fieldType, label, ocfDisplayId }) => {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Upload
            maxCount={1}
            beforeUpload={async (file) => {
              const fd = new FormData();
              fd.append("file", file);
              fd.append(
                "data",
                JSON.stringify({
                  ocfDisplayId,
                  fieldType,
                  fileName: file.name,
                })
              );
              const res = await axiosPrivate.post("/om/uploadFileForOcf", fd);
              field.onChange(res.data);
              return false;
            }}
            onRemove={async () => {

              if (field.value?.message) {
                const params = {
                  folder: "123456789",
                  fileName: field?.value?.message,
                };
                const options = {
                  url: "/om/delete",
                  method: "delete",
                  params,
                };
                await axiosPrivate(options);
              }
              field.onChange(null);
            }}
          >
            <Button icon={<UploadOutlined />}>{label}</Button>
          </Upload>
        )}
      />
    );
  };

  const [propertyList, setPropertyList] = useState([]);
  const [selectedProperty, setselectedProperty] = useState({});
  const getPropertyList = () => {
    const options = {
      method: "GET",
      url: "om/property",
    };
    axiosPrivate(options)
      .then((response) => {

        const activeProperty = response?.data?.data.filter((item) => item?.active == true);
        setPropertyList(activeProperty);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const mappedTypeSizes =
    (selectedProperty?.typeSize &&
      selectedProperty?.typeSize?.map((item) => ({
        type: item.type,
        size: item.size, // convert [1000,1200] -> "1000,1200"
      }))) ||
    [];

  const currentSize = watch("size");

  return (
    <MDBox mx={2}>
      <MDBox
        // mx={2}
        mb={4}
        py={2}
        px={2}
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <MDTypography variant="caption" color="white">
          Ocf Form : OCF123476543
        </MDTypography>
        <MDBox display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
          <MDTypography variant="caption" color="white">
            Status : Pending
          </MDTypography>
          <MDTypography variant="caption" color="white">
            Updated On : 12/12/26
          </MDTypography>
        </MDBox>
      </MDBox>

      <Steps current={current} items={stepItems} />
      <Divider />

      <form onSubmit={handleSubmit(onSubmit)}>
        {current === 0 && (
          <Row gutter={[16, 16]}>
            {/* Customer Name */}
            <Col span={8}>
              <div className="form-field">
                <label className="form-label">Customer Name</label>
                <Controller
                  name="applicant.name"
                  control={control}
                  render={({ field }) => (
                    <Input {...field} status={errors.applicant?.name && "error"} />
                  )}
                />
                <Error name="applicant.name" />
              </div>
            </Col>

            {/* Father/Husband Name */}
            <Col span={8}>
              <div className="form-field">
                <label className="form-label">Father/Husband Name</label>
                <Controller
                  name="applicant.careOfName"
                  control={control}
                  render={({ field }) => (
                    <Input {...field} status={errors.applicant?.careOfName && "error"} />
                  )}
                />
                <Error name="applicant.careOfName" />
              </div>
            </Col>

            {/* Email */}
            <Col span={8}>
              <div className="form-field">
                <label className="form-label">Customer Email</label>
                <Controller
                  name="applicant.email"
                  control={control}
                  render={({ field }) => (
                    <Input {...field} status={errors.applicant?.email && "error"} />
                  )}
                />
                <Error name="applicant.email" />
              </div>
            </Col>

            {/* Gender */}
            <Col span={8}>
              <div className="form-field">
                <label className="form-label">Gender</label>
                <Controller
                  name="applicant.gender"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      style={{ width: "100%" }}
                      placeholder="Select gender"
                      status={errors.applicant?.gender && "error"}
                    >
                      <Select.Option value="M">Male</Select.Option>
                      <Select.Option value="F">Female</Select.Option>
                    </Select>
                  )}
                />
                <Error name="applicant.gender" />
              </div>
            </Col>

            {/* Mobile */}
            <Col span={8}>
              <div className="form-field">
                <label className="form-label">Mobile</label>
                <Controller
                  name="applicant.mobile"
                  control={control}
                  render={({ field }) => (
                    <Input {...field} status={errors.applicant?.mobile && "error"} />
                  )}
                />
                <Error name="applicant.mobile" />
              </div>
            </Col>

            {/* Alternate Mobile */}
            <Col span={8}>
              <div className="form-field">
                <label className="form-label">Alternate Number</label>
                <Controller
                  name="applicant.mobile2"
                  control={control}
                  render={({ field }) => (
                    <Input {...field} status={errors.applicant?.mobile2 && "error"} />
                  )}
                />
                <Error name="applicant.mobile2" />
              </div>
            </Col>

            {/* DOB */}
            <Col span={8}>
              <div className="form-field">
                <label className="form-label">Date of Birth</label>
                <Controller
                  name="applicant.dob"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      style={{ width: "100%" }}
                      onChange={field.onChange}
                      status={errors.applicant?.dob && "error"}
                    />
                  )}
                />
                <Error name="applicant.dob" />
              </div>
            </Col>

            {/* Profession */}
            <Col span={8}>
              <div className="form-field">
                <label className="form-label">Profession</label>
                <Controller
                  name="applicant.profession"
                  control={control}
                  render={({ field }) => (
                    <Input {...field} status={errors.applicant?.profession && "error"} />
                  )}
                />
                <Error name="applicant.profession" />
              </div>
            </Col>

            {/* Address */}
            <Col span={8}>
              <div className="form-field">
                <label className="form-label">Address</label>
                <Controller
                  name="applicant.address"
                  control={control}
                  render={({ field }) => (
                    <Input {...field} status={errors.applicant?.address && "error"} />
                  )}
                />
                <Error name="applicant.address" />
              </div>
            </Col>

            {/* Aadhar */}
            <Col span={8}>
              <div className="form-field">
                <label className="form-label">Aadhar Number</label>
                <Controller
                  name="applicant.aadhar"
                  control={control}
                  render={({ field }) => (
                    <Input {...field} status={errors.applicant?.aadhar && "error"} />
                  )}
                />
                <Error name="applicant.aadhar" />

                <UploadField
                  name="applicant.aadharFile"
                  fieldType="customer_aadhar"
                  label="Aadhar File"
                  ocfDisplayId={"1234567890"}
                />
                <Error name="applicant.aadharFile" />
              </div>
            </Col>

            {/* PAN */}
            <Col span={8}>
              <div className="form-field">
                <label className="form-label">PAN Number</label>
                <Controller
                  name="applicant.pan"
                  control={control}
                  render={({ field }) => (
                    <Input {...field} status={errors.applicant?.pan && "error"} />
                  )}
                />
                <Error name="applicant.pan" />

                <Controller
                  name="applicant.panFile"
                  control={control}
                  render={({ field }) => (
                    <Upload
                      beforeUpload={() => false}
                      onChange={(info) => field.onChange(info.file)}
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />}>Select File</Button>
                    </Upload>
                  )}
                />
                <Error name="applicant.panFile" />
              </div>
            </Col>
          </Row>
        )}

        {/* STEP 2 */}
        {current === 1 && (
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <div className="form-field">
                <label className="form-label">Co-Applicant Available</label>
                <Controller
                  name="coApplicant.available"
                  control={control}
                  render={({ field }) => <Switch checked={field.value} onChange={field.onChange} />}
                />
              </div>
            </Col>

            {watch("coApplicant.available") && (
              <>
                {/* Name */}
                <Col span={8}>
                  <div className="form-field">
                    <label className="form-label">Co-Applicant Name</label>
                    <Controller
                      name="coApplicant.name"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} status={errors.coApplicant?.name && "error"} />
                      )}
                    />
                    <Error name="coApplicant.name" />
                  </div>
                </Col>

                {/* Father/Husband */}
                <Col span={8}>
                  <div className="form-field">
                    <label className="form-label">Father/Husband Name</label>
                    <Controller
                      name="coApplicant.careOfName"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} status={errors.coApplicant?.careOfName && "error"} />
                      )}
                    />
                    <Error name="coApplicant.careOfName" />
                  </div>
                </Col>

                {/* Email (optional) */}
                <Col span={8}>
                  <div className="form-field">
                    <label className="form-label">Email (Optional)</label>
                    <Controller
                      name="coApplicant.email"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} status={errors.coApplicant?.email && "error"} />
                      )}
                    />
                    <Error name="coApplicant.email" />
                  </div>
                </Col>

                {/* Gender */}
                <Col span={8}>
                  <div className="form-field">
                    <label className="form-label">Gender</label>
                    <Controller
                      name="coApplicant.gender"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          style={{ width: "100%" }}
                          placeholder="Select gender"
                          status={errors.coApplicant?.gender && "error"}
                        >
                          <Select.Option value="M">Male</Select.Option>
                          <Select.Option value="F">Female</Select.Option>
                        </Select>
                      )}
                    />
                    <Error name="coApplicant.gender" />
                  </div>
                </Col>

                {/* Mobile */}
                <Col span={8}>
                  <div className="form-field">
                    <label className="form-label">Mobile</label>
                    <Controller
                      name="coApplicant.mobile"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} status={errors.coApplicant?.mobile && "error"} />
                      )}
                    />
                    <Error name="coApplicant.mobile" />
                  </div>
                </Col>

                {/* Alternate Mobile */}
                <Col span={8}>
                  <div className="form-field">
                    <label className="form-label">Alternate Number</label>
                    <Controller
                      name="coApplicant.mobile2"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} status={errors.coApplicant?.mobile2 && "error"} />
                      )}
                    />
                    <Error name="coApplicant.mobile2" />
                  </div>
                </Col>

                {/* DOB */}
                <Col span={8}>
                  <div className="form-field">
                    <label className="form-label">Date of Birth</label>
                    <Controller
                      name="coApplicant.dob"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          style={{ width: "100%" }}
                          onChange={field.onChange}
                          status={errors.coApplicant?.dob && "error"}
                        />
                      )}
                    />
                    <Error name="coApplicant.dob" />
                  </div>
                </Col>

                {/* Profession */}
                <Col span={8}>
                  <div className="form-field">
                    <label className="form-label">Profession</label>
                    <Controller
                      name="coApplicant.profession"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} status={errors.coApplicant?.profession && "error"} />
                      )}
                    />
                    <Error name="coApplicant.profession" />
                  </div>
                </Col>

                {/* Address */}
                <Col span={8}>
                  <div className="form-field">
                    <label className="form-label">Address</label>
                    <Controller
                      name="coApplicant.address"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} status={errors.coApplicant?.address && "error"} />
                      )}
                    />
                    <Error name="coApplicant.address" />
                  </div>
                </Col>

                {/* Aadhar */}
                <Col span={8}>
                  <div className="form-field">
                    <label className="form-label">Aadhar Number</label>
                    <Controller
                      name="coApplicant.aadhar"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} status={errors.coApplicant?.aadhar && "error"} />
                      )}
                    />
                    <Error name="coApplicant.aadhar" />

                    <UploadField
                      name="coApplicant.aadharFile"
                      fieldType="coapplicant_aadhar"
                      label="Copy of Aadhar File"
                      ocfDisplayId={"1234567890"}
                    />
                    <Error name="coApplicant.aadharFile" />
                  </div>
                </Col>

                {/* PAN */}
                <Col span={8}>
                  <div className="form-field">
                    <label className="form-label">PAN Number</label>
                    <Controller
                      name="coApplicant.pan"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} status={errors.coApplicant?.pan && "error"} />
                      )}
                    />
                    <Error name="coApplicant.pan" />

                    <Controller
                      name="coApplicant.panFile"
                      control={control}
                      render={({ field }) => (
                        <Upload
                          beforeUpload={() => false}
                          onChange={(info) => field.onChange(info.file)}
                          maxCount={1}
                        >
                          <Button icon={<UploadOutlined />}>Copy of PAN File</Button>
                        </Upload>
                      )}
                    />
                    <Error name="coApplicant.panFile" />
                  </div>
                </Col>
              </>
            )}
          </Row>
        )}

        {/* STEP 3 */}
        {current === 2 && (
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <label>Property</label>
              <Controller
                name="propertyDetails"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    style={{ width: "100%" }}
                    placeholder="Select Property Details"
                    status={errors.propertyDetails && "error"}
                    onChange={(value, option) => {
                      field.onChange(value); // ✅ MUST — updates RHF state
                      const property = propertyList.find((p) => p.id === value);
                      if (!property) return;
                      setselectedProperty(property);
                      setValue("bsp", property?.bsp);
                    }}
                  >
                    {propertyList &&
                      propertyList.map((item) => (
                        <Select.Option value={item?.id}>{item?.name}</Select.Option>
                      ))}
                  </Select>
                )}
              />
              <Error name="propertyDetails" />
            </Col>

            <Col span={8}>
              <label>Type</label>
              <Controller
                name="size"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    style={{ width: "100%" }}
                    placeholder="Select Size"
                    status={errors.size && "error"}
                  >
                    {mappedTypeSizes &&
                      mappedTypeSizes.map((item) => (
                        <Select.Option value={item?.type}>{item?.type}</Select.Option>
                      ))}
                  </Select>
                )}
              />
              <Error name="size" />
            </Col>

            <Col span={8}>
              <label>Size</label>
              <Controller
                name="area"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    style={{ width: "100%" }}
                    placeholder="Select Size"
                    status={errors.size && "error"}
                  >
                    {mappedTypeSizes &&
                      currentSize &&
                      mappedTypeSizes
                        ?.find((item) => item.type === currentSize)
                        ?.size?.map((size) => (
                          <Select.Option key={size} value={size}>
                            {size}
                          </Select.Option>
                        ))}
                  </Select>
                )}
              />
              <Error name="area" />
            </Col>

            <Col span={8}>
              <label>Payment Plan</label>
              <Controller
                name="paymentPlan"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    style={{ width: "100%" }}
                    placeholder="Select Property Details"
                    status={errors.paymentPlan && "error"}
                  >
                    {selectedProperty?.paymentPlan &&
                      selectedProperty?.paymentPlan.map((item) => (
                        <Select.Option value={item}>{item}</Select.Option>
                      ))}
                  </Select>
                )}
              />
              <Error name="paymentPlan" />
            </Col>

            <Col span={8}>
              <label>BSP</label>
              <Controller
                name="bsp"
                control={control}
                render={({ field }) => <Input {...field} status={errors.bsp && "error"} />}
              />
              <Error name="bsp" />
            </Col>

            <Col span={8}>
              <label>BSP GST</label>
              <Controller
                name="bspGst"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    style={{ width: "100%" }}
                    placeholder="Select Size"
                    status={errors.bspGst && "error"}
                  >
                    <Select.Option value="M">Male</Select.Option>
                    <Select.Option value="F">Female</Select.Option>
                  </Select>
                )}
              />
              <Error name="bspGst" />
            </Col>

            <Col span={8}>
              <label>Other Charges </label>
              <Controller
                name="otherCharges"
                control={control}
                render={({ field }) => <Input {...field} status={errors.bsp && "error"} />}
              />
              <Error name="otherCharges" />
            </Col>

            <Col span={8}>
              <label>Other Charges GST</label>
              <Controller
                name="otherChargesGst"
                control={control}
                render={({ field }) => (
                  <Input {...field} status={errors.otherChargesGst && "error"} />
                )}
              />
              <Error name="otherChargesGst" />
            </Col>

            <Col span={8}>
              <label>PLC</label>
              <Controller
                name="plc"
                control={control}
                render={({ field }) => <Input {...field} status={errors.plc && "error"} />}
              />
              <Error name="plc" />
            </Col>

            <Col span={8}>
              <label>Revenue %</label>
              <Controller
                name="commission"
                control={control}
                render={({ field }) => <Input {...field} status={errors.commission && "error"} />}
              />
              <Error name="commission" />
            </Col>

            {/* Discount Switch */}
            <Col span={8}>
              <div className="form-field">
                <label className="form-label">Any Discount?</label>
                <Controller
                  name="commission.is_discount"
                  control={control}
                  render={({ field }) => <Switch checked={field.value} onChange={field.onChange} />}
                />
              </div>
            </Col>

            {/* ================= CONDITIONAL DISCOUNT FIELDS ================= */}

            {watch("commission.is_discount") && (
              <>
                <Col span={8}>
                  <label>Revenue Discount</label>
                  <Controller
                    name="commission.discount"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        {...field}
                        status={errors?.commission?.discount && "error"}
                      />
                    )}
                  />
                  <Error name="commission.discount" />
                </Col>

                <Col span={8}>
                  <div className="form-field">
                    <label className="form-label">Discount Approval File</label>
                    <Controller
                      name="commission.dis_approval_file"
                      control={control}
                      render={({ field }) => (
                        <Upload
                          beforeUpload={() => false}
                          maxCount={1}
                          onChange={(info) => field.onChange(info.file)}
                        >
                          <Button icon={<UploadOutlined />}>Upload File</Button>
                        </Upload>
                      )}
                    />
                  </div>
                  <Error name="commission.dis_approval_file" />
                </Col>
              </>
            )}

            <Col span={24}>
              <MDTypography variant="h6" fontWeight="bold">
                Inventory Details
              </MDTypography>
            </Col>

            <Col span={8}>
              <label>Unit No</label>
              <Controller
                name="inventoryDetail.unitNo"
                control={control}
                render={({ field }) => (
                  <Input {...field} status={errors?.inventoryDetail?.unitNo && "error"} />
                )}
              />
              <Error name="inventoryDetail.unitNo" />
            </Col>

            <Col span={8}>
              <label>Floor No</label>
              <Controller
                name="inventoryDetail.floorNo"
                control={control}
                render={({ field }) => (
                  <Input {...field} status={errors?.inventoryDetail?.floorNo && "error"} />
                )}
              />
              <Error name="inventoryDetail.floorNo" />
            </Col>

            <Col span={8}>
              <label>Tower/Block No</label>
              <Controller
                name="inventoryDetail.towerNo"
                control={control}
                render={({ field }) => (
                  <Input {...field} status={errors?.inventoryDetail?.towerNo && "error"} />
                )}
              />
              <Error name="inventoryDetail.towerNo" />
            </Col>
          </Row>
        )}

        {/* STEP 4 */}
        {current === 3 && (
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <label>Total BSP</label>
              <Controller
                name="totalBsp"
                control={control}
                render={({ field }) => <Input {...field} status={errors.totalBsp && "error"} />}
                disabled
              />
              <Error name="totalBsp" />
            </Col>

            <Col span={12}>
              <label>Property Price</label>
              <Controller
                name="price"
                control={control}
                render={({ field }) => <Input {...field} status={errors.price && "error"} />}
                disabled
              />
              <Error name="price" />
            </Col>

            {/* Commission Value */}
            <Col span={8}>
              <label>Slab</label>
              <Controller
                name="commission.value"
                control={control}
                render={({ field }) => (
                  <Input type="number" {...field} status={errors?.commission?.value && "error"} />
                )}
                disabled
              />
              <Error name="commission.value" />
            </Col>

            <Col span={8}>
              <label>Disount</label>
              <Controller
                name="commissionDiscount"
                control={control}
                render={({ field }) => (
                  <Input {...field} status={errors.commissionDiscount && "error"} />
                )}
                disabled
              />
              <Error name="commissionDiscount" />
            </Col>

            <Col span={8}>
              <label> Net Revenue</label>
              <Controller
                name="netIncome"
                control={control}
                render={({ field }) => <Input {...field} status={errors.netIncome && "error"} />}
                disabled
              />
              <Error name="netIncome" />
            </Col>

            <Col span={8}>
              <div className="form-field">
                <label className="form-label">Booking Form</label>
                <Controller
                  name="bookingForm"
                  control={control}
                  render={({ field }) => (
                    <Upload
                      beforeUpload={() => false}
                      onChange={(info) => field.onChange(info.file)}
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />}>Select File</Button>
                    </Upload>
                  )}
                />
                <Error name="bookingForm" />
              </div>
            </Col>

            <Col span={8}>
              <label> Remark</label>
              <Controller
                name="remark"
                control={control}
                render={({ field }) => <TextArea {...field} status={errors.netIncome && "error"} />}
              />
              <Error name="netIncome" />
            </Col>
          </Row>
        )}

        {current === 4 && (
          <Row gutter={[16, 16]}>
            <Col span={24} style={{ display: "flex", justifyContent: "space-between" }}>
              <h3>Payments</h3>

              <Button
                type="primary"
                onClick={() =>
                  addPayment({
                    type: "",
                    amount: "",
                    receiptNo: "",
                    confirmation: false,
                    date: "",
                  })
                }
              >
                + Add More Payment
              </Button>
            </Col>

            {paymentFields.map((item, index) => (
              <React.Fragment key={item.id}>
                <Col span={24}>
                  <div style={{ fontWeight: 600 }}>
                    Payment #{index + 1}
                    <Button
                      danger
                      size="small"
                      style={{ marginLeft: 12 }}
                      onClick={() => removePayment(index)}
                    >
                      Remove
                    </Button>
                  </div>
                </Col>

                {/* Type */}
                <Col span={8}>
                  <label>Payment Type</label>
                  <Controller
                    name={`payment.${index}.type`}
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        style={{ width: "100%" }}
                        placeholder="Select Payment confirmation"
                        status={errors.paymentType && "error"}
                      >
                        <Select.Option value="M">Yes</Select.Option>
                        <Select.Option value="F">No</Select.Option>
                      </Select>
                    )}
                  />
                  <Error name={`payment.${index}.type`} />
                </Col>

                <Col span={8}>
                  <label>Payment Date</label>
                  <Controller
                    name={`payment.${index}.date`}
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        style={{ width: "100%" }}
                        onChange={(_, dateStr) => field.onChange(dateStr)}
                      />
                    )}
                  />
                  <Error name={`payment.${index}.date`} />
                </Col>

                {/* Amount */}
                <Col span={8}>
                  <label>Amount</label>
                  <Controller
                    name={`payment.${index}.amount`}
                    control={control}
                    render={({ field }) => <Input {...field} placeholder="Amount" type="number" />}
                  />
                  <Error name={`payment.${index}.amount`} />
                </Col>

                {/* Receipt No */}
                <Col span={8}>
                  <div className="form-field">
                    <label className="form-label">Payment Details (Txn No/Cheque No)</label>
                    <Controller
                      name={`payment.${index}.receiptNo`}
                      control={control}
                      render={({ field }) => <Input {...field} placeholder="Receipt No" />}
                    />

                    <UploadField
                      name="applicant.aadharFile"
                      fieldType={index + "payment-reciept-number"}
                      label="Payment Proof File"
                      ocfDisplayId={"1234567890"}
                    />
                    <Error name={`payment.${index}.receiptNo`} />
                  </div>
                </Col>

                {/* Confirmation */}
                <Col span={8}>
                  <label></label>
                  <Controller
                    name={`payment.${index}.confirmation`}
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      >
                        Payment Confirmed
                      </Checkbox>
                    )}
                  />
                </Col>

                <Col span={8}>
                  <div className="form-field">
                    <label className="form-label">Email Confirmation from Builder </label>

                    <UploadField
                      name="applicant.aadharFile"
                      fieldType={index + "payment-reciept-number"}
                      label="Email Confirmation File"
                      ocfDisplayId={"1234567890"}
                    />
                    <Error name={`payment.${index}.receiptNo`} />
                  </div>
                </Col>
              </React.Fragment>
            ))}
          </Row>
        )}

        <Divider />

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {current > 0 ? <Button onClick={prev}>Previous</Button> : <span></span>}

          {current < LAST_STEP && (
            <Button type="primary" onClick={next}>
              Save & Next
            </Button>
          )}

          {current === LAST_STEP && (
            <Button type="primary" htmlType="submit">
              Submit OCF
            </Button>
          )}
        </div>
      </form>
    </MDBox>
  );
};

export default OCFMultiStepForm;
